import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Alert, Spinner, Modal, Form, Badge, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import bee123ExportService from '../services/bee123Export';

const BEE123ExportDashboard = () => {
  const [selectedLearner, setSelectedLearner] = useState('');
  const [learners, setLearners] = useState([]);
  const [compiledData, setCompiledData] = useState(null);
  const [documentStatus, setDocumentStatus] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLearners, setLoadingLearners] = useState(true);
  const [error, setError] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [activeTab, setActiveTab] = useState('compilation');

  useEffect(() => {
    loadLearners();
  }, []);

  useEffect(() => {
    if (selectedLearner) {
      loadLearnerData();
    }
  }, [selectedLearner]);

  const loadLearners = async () => {
    try {
      setLoadingLearners(true);
      const usersQuery = query(collection(db, 'users'), where('role', '==', 'learner'));
      const snapshot = await getDocs(usersQuery);
      const learnerList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().displayName || doc.data().email || 'Learner',
        idNumber: doc.data().idNumber || 'N/A'
      }));
      setLearners(learnerList);
      if (learnerList.length > 0) {
        setSelectedLearner(learnerList[0].id);
      }
    } catch (error) {
      setError('Failed to load learners: ' + error.message);
    } finally {
      setLoadingLearners(false);
    }
  };

  const loadLearnerData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [dataResult, docsResult, historyResult] = await Promise.all([
        bee123ExportService.compileLearnerData(selectedLearner),
        bee123ExportService.gatherRequiredDocuments(selectedLearner),
        bee123ExportService.getSubmissionHistory(selectedLearner)
      ]);

      if (dataResult.success) {
        setCompiledData(dataResult.data);
        
        const validationResult = await bee123ExportService.validateSubmissionData(dataResult.data);
        if (validationResult.success) {
          setValidationResult(validationResult.data);
        }
      } else {
        setError(dataResult.error);
      }

      if (docsResult.success) {
        setDocumentStatus(docsResult.data);
      }

      if (historyResult.success) {
        setSubmissionHistory(historyResult.data);
      }
    } catch (error) {
      setError('Failed to load learner data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmission = async () => {
    if (!compiledData || !validationResult?.isValid) {
      setError('Data validation must pass before submission');
      return;
    }

    setLoading(true);
    
    try {
      const result = await bee123ExportService.submitToBEE123(compiledData);
      
      if (result.success) {
        setShowSubmissionModal(false);
        alert(`Successfully submitted to BEE123! Reference: ${result.data.bee123Reference}`);
        loadLearnerData();
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Submission failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { variant: 'primary', text: 'Submitted' },
      processing: { variant: 'warning', text: 'Processing' },
      approved: { variant: 'success', text: 'Approved' },
      rejected: { variant: 'danger', text: 'Rejected' },
      pending: { variant: 'secondary', text: 'Pending' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getDocumentStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'Present';
      case 'missing': return 'Missing';
      case 'unverified': return 'Unverified';
      default: return 'Unknown';
    }
  };

  const calculateCompletionPercentage = () => {
    if (!documentStatus) return 0;
    
    const totalDocs = documentStatus.documentStatus.length;
    const completeDocs = documentStatus.documentStatus.filter(doc => doc.present && doc.verified).length;
    
    return Math.round((completeDocs / totalDocs) * 100);
  };

  return (
    <div className="bee123-export-dashboard">
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5>BEE123 Export Dashboard</h5>
            <Form.Select
              value={selectedLearner}
              onChange={(e) => setSelectedLearner(e.target.value)}
              style={{ width: '250px' }}
              disabled={loadingLearners || learners.length === 0}
            >
              {learners.length === 0 && (
                <option value="">No learners available</option>
              )}
              {learners.map(learner => (
                <option key={learner.id} value={learner.id}>
                  {learner.name} ({learner.idNumber})
                </option>
              ))}
            </Form.Select>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {!loadingLearners && learners.length === 0 && (
            <Alert variant="warning">
              No learners found to compile BEE123 submissions.
            </Alert>
          )}
          
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
              <p>Loading learner data...</p>
            </div>
          ) : (
            <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
              <Tab eventKey="compilation" title="Data Compilation">
                {compiledData && (
                  <div>
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <Card className="h-100">
                          <Card.Header>Personal Information</Card.Header>
                          <Card.Body>
                            <Table size="sm" borderless>
                              <tbody>
                                <tr>
                                  <td><strong>ID Number:</strong></td>
                                  <td>{compiledData.personalInformation.idNumber}</td>
                                </tr>
                                <tr>
                                  <td><strong>Name:</strong></td>
                                  <td>{compiledData.personalInformation.firstName} {compiledData.personalInformation.lastName}</td>
                                </tr>
                                <tr>
                                  <td><strong>Email:</strong></td>
                                  <td>{compiledData.personalInformation.contactDetails?.email}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>
                      </div>
                      <div className="col-md-6">
                        <Card className="h-100">
                          <Card.Header>Training Information</Card.Header>
                          <Card.Body>
                            <Table size="sm" borderless>
                              <tbody>
                                <tr>
                                  <td><strong>Program:</strong></td>
                                  <td>{compiledData.trainingInformation.programName}</td>
                                </tr>
                                <tr>
                                  <td><strong>NQF Level:</strong></td>
                                  <td>{compiledData.trainingInformation.nqfLevel}</td>
                                </tr>
                                <tr>
                                  <td><strong>Credits:</strong></td>
                                  <td>{compiledData.trainingInformation.credits}</td>
                                </tr>
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>
                      </div>
                    </div>

                    <Card className="mb-3">
                      <Card.Header>Attendance Summary</Card.Header>
                      <Card.Body>
                        {compiledData.attendanceRecord.length > 0 ? (
                          <div>
                            <p>Total Sessions: {compiledData.attendanceRecord.length}</p>
                            <p>Total Hours: {compiledData.attendanceRecord.reduce((sum, record) => sum + (record.hours || 0), 0)}</p>
                            <Table striped size="sm">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Module</th>
                                  <th>Status</th>
                                  <th>Hours</th>
                                  <th>Teams Verified</th>
                                </tr>
                              </thead>
                              <tbody>
                                {compiledData.attendanceRecord.slice(0, 5).map((record, index) => (
                                  <tr key={index}>
                                    <td>{record.date}</td>
                                    <td>{record.module}</td>
                                    <td>{getStatusBadge(record.status)}</td>
                                    <td>{record.hours}</td>
                                    <td>{record.teamsVerified ? 'Yes' : 'No'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            {compiledData.attendanceRecord.length > 5 && (
                              <p className="text-muted">... and {compiledData.attendanceRecord.length - 5} more records</p>
                            )}
                          </div>
                        ) : (
                          <Alert variant="warning">No attendance records found</Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                )}
              </Tab>

              <Tab eventKey="documents" title="Document Status">
                {documentStatus && (
                  <div>
                    <div className="mb-3">
                      <h6>Document Completion Progress</h6>
                      <ProgressBar 
                        now={calculateCompletionPercentage()} 
                        label={`${calculateCompletionPercentage()}%`}
                        variant={calculateCompletionPercentage() === 100 ? 'success' : 'warning'}
                      />
                    </div>

                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Document Type</th>
                          <th>Status</th>
                          <th>Present</th>
                          <th>Verified</th>
                          <th>File Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentStatus.documentStatus.map((doc, index) => (
                          <tr key={index} className={!doc.present ? 'table-danger' : !doc.verified ? 'table-warning' : 'table-success'}>
                            <td>{doc.type.replace(/_/g, ' ').toUpperCase()}</td>
                            <td>
                              {getDocumentStatusLabel(doc.present ? (doc.verified ? 'present' : 'unverified') : 'missing')}
                            </td>
                            <td>{doc.present ? 'Yes' : 'No'}</td>
                            <td>{doc.verified ? 'Yes' : 'No'}</td>
                            <td>{doc.document?.fileName || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {documentStatus.missingDocuments.length > 0 && (
                      <Alert variant="danger">
                        <strong>Missing Documents:</strong>
                        <ul className="mb-0 mt-2">
                          {documentStatus.missingDocuments.map((doc, index) => (
                            <li key={index}>{doc.type.replace(/_/g, ' ').toUpperCase()}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    {documentStatus.unverifiedDocuments.length > 0 && (
                      <Alert variant="warning">
                        <strong>Unverified Documents:</strong>
                        <ul className="mb-0 mt-2">
                          {documentStatus.unverifiedDocuments.map((doc, index) => (
                            <li key={index}>{doc.type.replace(/_/g, ' ').toUpperCase()}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}
                  </div>
                )}
              </Tab>

              <Tab eventKey="validation" title="Validation">
                {validationResult && (
                  <div>
                    <div className="mb-3">
                      <h6>Validation Status</h6>
                      <Alert variant={validationResult.isValid ? 'success' : 'danger'}>
                        {validationResult.isValid ? 
                          'Data passes validation. Ready for submission.' : 
                          'Validation errors must be resolved before submission.'
                        }
                      </Alert>
                    </div>

                    {validationResult.errors.length > 0 && (
                      <Alert variant="danger">
                        <strong>Validation Errors:</strong>
                        <ul className="mb-0 mt-2">
                          {validationResult.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    {validationResult.warnings.length > 0 && (
                      <Alert variant="warning">
                        <strong>Validation Warnings:</strong>
                        <ul className="mb-0 mt-2">
                          {validationResult.warnings.map((warning, index) => (
                            <li key={index}>{warning}</li>
                          ))}
                        </ul>
                      </Alert>
                    )}

                    {validationResult.isValid && (
                      <div className="text-center mt-4">
                        <Button 
                          variant="primary" 
                          size="lg"
                          onClick={() => setShowSubmissionModal(true)}
                          disabled={loading}
                        >
                          Submit to BEE123
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Tab>

              <Tab eventKey="history" title="Submission History">
                {submissionHistory.length > 0 ? (
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Submission ID</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>BEE123 Reference</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissionHistory.map((submission, index) => (
                        <tr key={index}>
                          <td>{submission.submissionId}</td>
                          <td>{new Date(submission.submissionDate).toLocaleDateString()}</td>
                          <td>{getStatusBadge(submission.status)}</td>
                          <td>{submission.bee123Reference}</td>
                          <td>
                            <Button 
                              size="sm" 
                              variant="outline-primary"
                              onClick={() => bee123ExportService.trackSubmissionStatus(submission.submissionId)}
                            >
                              Track Status
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="info">No submissions found for this learner.</Alert>
                )}
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>

      <Modal show={showSubmissionModal} onHide={() => setShowSubmissionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm BEE123 Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to submit this learner's data to the BEE123 system?</p>
          <p className="text-muted">
            This action will compile all learner data, documents, and attendance records 
            for official submission to the BEE123 levy claim system.
          </p>
          {compiledData && (
            <div className="bg-light p-3 rounded">
              <strong>Learner:</strong> {compiledData.personalInformation.firstName} {compiledData.personalInformation.lastName}<br />
              <strong>ID Number:</strong> {compiledData.personalInformation.idNumber}<br />
              <strong>Program:</strong> {compiledData.trainingInformation.programName}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSubmissionModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmission} disabled={loading}>
            {loading ? <Spinner size="sm" /> : 'Confirm Submission'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BEE123ExportDashboard;
